import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Alert, AlertDescription } from "./ui/alert"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "./ui/tooltip"
import { useToast } from "./ui/use-toast"
import sharingService from '../services/sharingService';
import {
    Folder,
    Link as LinkIcon,
    X,
    Copy,
    CheckCircle,
    Loader2,
    Info
} from 'lucide-react';

const ShareFolderDialog = ({ open, onClose, folder }) => {
    const [shareUrl, setShareUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');
    const { toast } = useToast();

    const inputRef = useRef(null);

    useEffect(() => {
        if (open && folder) {
            loadShareLink();
        }
        setCopied(false);
    }, [open, folder]);

    useEffect(() => {
        if (shareUrl && inputRef.current) {
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 100);
        }
    }, [shareUrl]);

    const loadShareLink = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await sharingService.getFolderShareLinkInfo(folder.id);

            if (response.hasShareLink) {
                setShareUrl(response.shareUrl);
            } else {
                const createResponse = await sharingService.createFolderShareLink(folder.id);
                setShareUrl(createResponse.shareUrl);
            }
        } catch (err) {
            console.error('Error loading folder share link:', err);
            setError(err.response?.data?.message || 'Gagal membuat link');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = async () => {
        if (!shareUrl) {
            toast({
                variant: "destructive",
                title: "Error",
                description: "Link belum tersedia"
            });
            return;
        }

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                toast({
                    title: "Berhasil",
                    description: "Link berhasil disalin!"
                });
                setTimeout(() => setCopied(false), 3000);
                return;
            }

            // Fallback
            if (inputRef.current) {
                inputRef.current.select();
                document.execCommand('copy');
                setCopied(true);
                toast({
                    title: "Berhasil",
                    description: "Link berhasil disalin!"
                });
                setTimeout(() => setCopied(false), 3000);
            }
        } catch (err) {
            console.error('Copy error:', err);
            toast({
                variant: "destructive",
                title: "Gagal",
                description: "Gagal menyalin link. Silakan salin secara manual."
            });
        }
    };

    const handleRevoke = async () => {
        try {
            setLoading(true);
            await sharingService.revokeFolderShareLink(folder.id);
            setShareUrl('');
            toast({
                title: "Berhasil",
                description: "Link berhasil dicabut"
            });
            onClose();
        } catch (err) {
            console.error('Error revoking link:', err);
            toast({
                variant: "destructive",
                title: "Gagal",
                description: "Gagal mencabut link"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Folder className="h-5 w-5 text-yellow-500" />
                        Bagikan Folder
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <>
                            <div className="mb-2 text-sm text-gray-600">
                                Folder: <span className="font-semibold text-gray-900">{folder?.name}</span>
                            </div>

                            {shareUrl && (
                                <>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            ref={inputRef}
                                            value={shareUrl}
                                            readOnly
                                            className="font-mono text-sm bg-gray-50"
                                            onClick={(e) => e.target.select()}
                                        />
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        size="icon"
                                                        variant={copied ? "default" : "outline"}
                                                        className={copied ? "bg-green-600 hover:bg-green-700" : ""}
                                                        onClick={handleCopyLink}
                                                    >
                                                        {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{copied ? "Tersalin!" : "Salin Link"}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>

                                    <Alert className="bg-blue-50 text-blue-800 border-blue-200">
                                        <Info className="h-4 w-4" />
                                        <AlertDescription className="ml-2 text-xs">
                                            Link ini dapat diakses siapa saja yang memilikinya. Folder dan semua isinya dapat dilihat dan diunduh tanpa login.
                                        </AlertDescription>
                                    </Alert>

                                    <div className="p-3 bg-gray-50 rounded-lg border text-xs text-gray-600">
                                        <p className="font-semibold text-primary mb-1">💡 Cara Menyalin Link:</p>
                                        <p>• Klik tombol Copy di sebelah kanan, atau</p>
                                        <p>• Klik kotak link → Tekan Ctrl+C (Windows) atau Cmd+C (Mac)</p>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    {shareUrl && (
                        <Button variant="destructive" onClick={handleRevoke} disabled={loading} className="mr-auto">
                            Cabut Link
                        </Button>
                    )}
                    <Button variant="ghost" onClick={handleClose} disabled={loading}>
                        Tutup
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ShareFolderDialog;
